/**
 * apiUrl / authUrl — compose full URLs for `/api/*` fetches.
 *
 * TWO bases now exist, because auth and non-auth traffic diverge in the
 * embedded plugin (cloud-SpaSource mode):
 *
 *  - API_BASE  (apiUrl):  the LOCAL sidecar. Bridge, modules, downloads — all
 *    the local-data calls — go here. In cloud-SpaSource mode the WebView2 host
 *    appends `?apiBase=http%3A%2F%2Flocalhost%3A<port>` so every non-auth fetch
 *    is redirected at the sidecar instead of the cloud origin that served the
 *    SPA. Bundled / dev-server mode: `apiBase` is absent → empty prefix →
 *    behaviour is byte-identical to same-origin.
 *
 *  - AUTH_BASE (authUrl): the CLOUD. Auth calls (login/register/refresh/status/
 *    forgot-password/oauth) go DIRECT to the cloud — the website already does
 *    this with the same client and a proven contract. We KILLED the sidecar
 *    auth proxy. In the plugin's cloud-SpaSource mode `window.location.origin`
 *    IS the cloud (the SPA was served from app.gamedevbuddy.com /
 *    app.cvcstaging.com), so authUrl resolves same-origin → no CORS.
 *
 * Both bases are read ONCE at module load. The C++ shell controls the URLs;
 * there is no runtime setter and no live re-read. Switching modes requires a
 * WebView reload.
 *
 * API_BASE persistence: the `?apiBase` query param is persisted to
 * localStorage on first read and falls back to that persisted value when the
 * param is absent. An OAuth full-page redirect (the WebView2 popup-blocked
 * fallback) bounces through the cloud and lands back WITHOUT the query string,
 * which would otherwise drop the sidecar base and silently route every
 * subsequent non-auth fetch at the cloud. Persisting keeps the sidecar base
 * alive across that round-trip.
 */

/** localStorage key the sidecar base is cached under (survives OAuth redirect). */
const API_BASE_KEY = 'gdb_api_base'

/** Strip a single trailing slash so joins never produce '<base>//api/x'. */
function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value
}

function resolveApiBase(): string {
  // SSR / jsdom safety — vitest runs with a window, but be defensive.
  if (typeof window === 'undefined') return ''

  const param = new URLSearchParams(window.location.search).get('apiBase')

  // Param present → it wins, and we persist it so a later query-stripped
  // navigation (OAuth full-page redirect) can still recover the sidecar base.
  if (param) {
    const base = stripTrailingSlash(param)
    try {
      window.localStorage.setItem(API_BASE_KEY, base)
    } catch {
      // localStorage may be unavailable (private mode / blocked) — non-fatal,
      // the base still works for this page load, we just can't persist it.
    }
    return base
  }

  // Param absent → fall back to the persisted value (if any). This is what
  // keeps the sidecar base alive across an OAuth redirect that dropped the
  // query string. Empty/missing → same-origin (empty prefix), as before.
  try {
    const persisted = window.localStorage.getItem(API_BASE_KEY)
    if (persisted) return stripTrailingSlash(persisted)
  } catch {
    // Ignore — fall through to same-origin.
  }

  return ''
}

/**
 * Resolve the AUTH base — where login/register/status/oauth calls go.
 *
 * Precedence:
 *   1. `?authBase=` query param (explicit override, e.g. bundled-mode testing)
 *   2. `import.meta.env.VITE_AUTH_BASE` (dev / build-time pin)
 *   3. `window.location.origin` (cloud-SpaSource same-origin — the default)
 */
function resolveAuthBase(): string {
  // SSR / jsdom safety.
  if (typeof window === 'undefined') return ''

  const param = new URLSearchParams(window.location.search).get('authBase')
  if (param) return stripTrailingSlash(param)

  const envBase = import.meta.env.VITE_AUTH_BASE
  if (typeof envBase === 'string' && envBase.length > 0) {
    return stripTrailingSlash(envBase)
  }

  // Default: same origin as the SPA. In the plugin's cloud-SpaSource mode the
  // origin IS the cloud, so auth lands on the cloud with no CORS. In the
  // website / dev-server it's the current host (Vite proxies /api/auth/*).
  return stripTrailingSlash(window.location.origin)
}

const API_BASE: string = resolveApiBase()
const AUTH_BASE: string = resolveAuthBase()

/**
 * Compose the full URL for a (non-auth) API request — bridge, modules,
 * downloads, workspace. Pass the path starting with '/api/...'. Returns either
 * the path unchanged (bundled / same-origin) or `<apiBase><path>` when running
 * in cloud-SPA mode (or after recovering the persisted sidecar base).
 */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`
}

/**
 * Compose the full URL for an AUTH request — login/register/refresh/status/
 * forgot-password/oauth. Always targets the cloud (or same-origin), NEVER the
 * sidecar. Pass the path starting with '/api/auth/...'.
 */
export function authUrl(path: string): string {
  return `${AUTH_BASE}${path}`
}

/**
 * Build the bridge WebSocket URL from a resolved sidecar HTTP base.
 *
 * The UE plugin's bridge WS lives on the SAME port as the sidecar's REST API
 * (a `/ws/bridge` upgrade route), and the C++ shell injects that real port via
 * `?apiBase=http://localhost:<port>`. Deriving the WS URL from the sidecar base
 * keeps the bridge in lock-step with the REST port instead of a separate,
 * drift-prone default — the old hardcoded `3798` vs the actual `3799` was
 * exactly that drift, and it left the plugin permanently "UE Disconnected".
 *
 * Pure helper (kept separate from `bridgeWsUrl`) so it's unit-testable without
 * touching the module-frozen `API_BASE`. `http→ws`, `https→wss`.
 */
export function toBridgeWsUrl(apiBase: string, fallbackPort: string): string {
  if (apiBase) {
    return `${apiBase.replace(/^http/, 'ws')}/ws/bridge`
  }
  // No sidecar base (dev server / website). The website never connects
  // (BridgeProvider gates on `/embed/*`); dev falls back to the local sidecar.
  return `ws://127.0.0.1:${fallbackPort}/ws/bridge`
}

/** Resolve the bridge WS URL for this load — derived from the sidecar API base. */
export function bridgeWsUrl(): string {
  const fallbackPort = String(import.meta.env.VITE_API_PORT || '3799')
  return toBridgeWsUrl(API_BASE, fallbackPort)
}

/**
 * Re-exported for diagnostics + tests. Do NOT mutate.
 * The C++ shell can read these from devtools to verify cloud-mode wiring.
 */
export const __INTERNAL_API_BASE__ = API_BASE
export const __INTERNAL_AUTH_BASE__ = AUTH_BASE
